/**
 * Der Greenscreen (S11), einmal gerechnet.
 *
 * **Bis zum 22. September stand er nur im Export** (7.144): `compile.ts`
 * setzte `chromakey` in die Kette, die Vorschau kannte den Effekt auf keinem
 * ihrer beiden Wege. Wer ein grünes Tuch wegschnitt, sah in der Vorschau das
 * Tuch und im Film das Loch -- genau der Abstand, den 7.69 als Messlatte
 * nennt.
 *
 * Gerechnet wird, was `chromakey` rechnet: der Abstand zur Schlüsselfarbe in
 * den beiden Farbdifferenzkanälen U und V, und daraus die Deckung. Die
 * Formel steht hier und nicht in drei Abschriften; der Shader auf der
 * Grafikkarte hat dieselben Zeilen in GLSL, und dass beide dasselbe ergeben,
 * misst der GL-Prüfstand an Zahlen.
 */
/**
 * Die Matrix, mit der ein Bild in U und V umgerechnet wird: BT.601 mit
 * begrenztem Umfang, also die Faktoren mal 224/255.
 *
 * **Und genau die muss es sein** (7.145). `chromakey` vergleicht die
 * Farbdifferenzkanäle des Bildes -- und die legt ffmpeg mit dieser Matrix
 * hin. Die Schlüsselfarbe rechnete es bis zum 22. September mit der *anderen*
 * um, der für vollen Umfang, und verglich dann Äpfel mit Birnen. Gemessen:
 * ein Bild in 0x00b140 gegen den Schlüssel 0x00b140 ergibt keinen Abstand
 * null, sondern 0,027 -- die Farbe blieb unter einer Toleranz von 0,028
 * einfach stehen. Deshalb bekommt `chromakey` den Schlüssel jetzt schon
 * fertig umgerechnet (`yuv=1`).
 *
 * Die Summe jeder Zeile ist null: jedes Grau liegt damit genau auf der
 * Mitte.
 */
export const U_MATRIX = [-0.1482265, -0.2909892, 0.4392157];
export const V_MATRIX = [0.4392157, -0.3677890, -0.0714267];
/** Dieselbe Umrechnung für die Helligkeit; `chromakey` sieht sie nicht an. */
export const Y_MATRIX = [0.2567882, 0.5041294, 0.0979059];
/** Die Mitte der beiden Farbdifferenzkanäle in acht Bit. */
export const CHROMA_MITTE = 128;
/** `#rrggbb` als drei Anteile 0..1. */
export function rgbVon(hex) {
    const h = hex.replace('#', '');
    const voll = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.padEnd(6, '0');
    const n = Number.parseInt(voll.slice(0, 6), 16);
    if (!Number.isFinite(n))
        return [0, 0, 0];
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}
/**
 * Y, U und V einer Farbe in acht Bit -- so, wie sie im Bild stehen.
 *
 * Gerundet wird, weil im Film gerundet dasteht: ein Bild in yuva420p trägt
 * ganze Zahlen. Wer hier in Bruchteilen weiterrechnete, bekäme an der
 * weichen Kante einen anderen Wert als der Film, und genau darum geht es.
 */
export function chromaYUV8(rgb) {
    const kanal = (m, mitte) => Math.round(((m[0] ?? 0) * rgb[0] + (m[1] ?? 0) * rgb[1] + (m[2] ?? 0) * rgb[2]) * 255 + mitte);
    return [kanal(Y_MATRIX, 16), kanal(U_MATRIX, CHROMA_MITTE), kanal(V_MATRIX, CHROMA_MITTE)];
}
/** Nur die beiden Farbdifferenzkanäle, in acht Bit. */
export function chromaUV8(rgb) {
    const [, u, v] = chromaYUV8(rgb);
    return [u, v];
}
/**
 * Der Abstand zur Schlüsselfarbe, so wie `do_chromakey_pixel` ihn bildet:
 * die Länge in der UV-Ebene, geteilt durch die längstmögliche (255 mal
 * Wurzel zwei). 0 heisst „dieselbe Farbe“, 1 heisst „so weit weg wie
 * möglich“.
 */
export function chromaAbstand(uv, schluessel) {
    const du = uv[0] - schluessel[0];
    const dv = uv[1] - schluessel[1];
    return Math.sqrt((du * du + dv * dv) / 2) / 255;
}
/**
 * Was von der Ebene stehenbleibt: 0 heisst weggeschnitten, 1 heisst
 * unberührt.
 *
 * Unterhalb der Toleranz ist die Farbe die Schlüsselfarbe und fällt weg;
 * darüber wird über `weich` aufgeblendet. Ohne `weich` ist es eine Kante --
 * so hält es auch ffmpeg, das unterhalb von 0,0001 gar nicht erst mischt.
 */
export function chromaDeckung(rgb, schluessel, toleranz, weich) {
    const abstand = chromaAbstand(chromaUV8(rgb), schluessel);
    if (weich <= 0.0001)
        return abstand > toleranz ? 1 : 0;
    return Math.min(1, Math.max(0, (abstand - toleranz) / weich));
}
/**
 * Die Schlüsselfarbe so, wie `chromakey=...:yuv=1` sie liest: `0xYYUUVV`.
 *
 * Mit `yuv=1` nimmt der Filter die drei Bytes als Y, U und V und rechnet
 * nichts mehr um -- damit steht auf beiden Seiten des Vergleichs dieselbe
 * Matrix (7.145).
 */
export function chromaSchluesselFuerFfmpeg(hex) {
    const [y, u, v] = chromaYUV8(rgbVon(hex));
    const zwei = (n) => Math.min(255, Math.max(0, n)).toString(16).padStart(2, '0');
    return `0x${zwei(y)}${zwei(u)}${zwei(v)}`;
}
//# sourceMappingURL=chroma.js.map